<template>
    <Head title="Demo Accounts" />
    <BaseLayout gap="lg" fill-height>
        <BasePageHeader
            title="Demo accounts"
            subtitle="Create a stand-in persona to show Nadia to colleagues, without touching your own conversations or account."
            subtitle-below />

        <section aria-label="About demo accounts" class="rounded-lg border border-border bg-surface-sunken p-4">
            <p class="body-small text-ink-muted">
                Demo accounts are for demos only. Use them to show Nadia or try unreleased features without changing your main account. Avoid entering real people, confidential
                details, or sensitive situations. When you're done, use <span class="body-small-semibold">Exit Demo Mode</span>.
            </p>
        </section>

        <p v-if="formError" role="alert" class="body-small text-chip-status-overdue-text">{{ formError }}</p>

        <section aria-labelledby="demo-accounts-list-heading" class="flex flex-col gap-4">
            <h2 id="demo-accounts-list-heading" class="title-section">{{ listHeading }}</h2>

            <p v-if="!demo_accounts.length" class="body-small text-ink-muted">You don't have any demo accounts yet. Create one below to get started.</p>

            <ul v-else class="flex flex-col gap-3">
                <li v-for="account in demo_accounts" :key="account.id">
                    <BaseCard class="flex flex-wrap items-center justify-between gap-4">
                        <div class="min-w-0 flex-1 flex flex-col gap-1">
                            <p class="body-semibold text-ink truncate" :title="account.label">{{ account.label }}</p>
                            <p class="body-small text-ink-muted truncate" :title="[account.job_title, account.organization_name].filter(Boolean).join(' · ')">
                                {{ [account.job_title, account.organization_name].filter(Boolean).join(" · ") }}
                            </p>
                            <p class="body-small text-ink-muted">{{ lastUsedLabel(account) }}</p>
                        </div>
                        <!-- A native form POST, not router.post. Entering a demo calls
                             auth_login, which rotates the CSRF secret; an Inertia XHR would
                             swap the page without re-rendering the document, leaving the
                             stale <meta name="csrf-token"> in place and 403-ing the next
                             write until a manual refresh. A real navigation reloads the
                             document and picks up the new token. MasqueradeBanner exits the
                             same way, for the same reason. -->
                        <form class="shrink-0" method="post" :action="account.masquerade_url">
                            <input type="hidden" name="csrfmiddlewaretoken" :value="csrfToken" />
                            <BaseButton theme="secondary" type="submit">Enter demo</BaseButton>
                        </form>
                    </BaseCard>
                </li>
            </ul>
        </section>

        <section aria-labelledby="demo-account-create-heading" class="flex flex-col gap-4">
            <h2 id="demo-account-create-heading" class="title-section">Create a demo account</h2>

            <p v-if="atLimit" class="body-small text-ink-muted">{{ atLimitMessage }}</p>

            <form v-else class="flex flex-col gap-6" @submit.prevent="submit">
                <div class="grid gap-6 md:grid-cols-2">
                    <FormElement title="First name" for="demo-first-name" required>
                        <input
                            id="demo-first-name"
                            v-model="form.first_name"
                            type="text"
                            required
                            :maxlength="text_max_length"
                            class="body-small font-normal focus-field rounded-lg p-2 w-full border border-control-border user-invalid:border-button-danger" />
                    </FormElement>
                    <FormElement title="Last name" for="demo-last-name" required>
                        <input
                            id="demo-last-name"
                            v-model="form.last_name"
                            type="text"
                            required
                            :maxlength="text_max_length"
                            class="body-small font-normal focus-field rounded-lg p-2 w-full border border-control-border user-invalid:border-button-danger" />
                    </FormElement>
                </div>

                <FormElement title="Role" for="demo-job-title" required>
                    <input
                        id="demo-job-title"
                        v-model="form.job_title"
                        type="text"
                        required
                        :maxlength="text_max_length"
                        placeholder="e.g. Regional Sales Manager"
                        class="body-small font-normal focus-visible:outline-hidden rounded-lg p-2 w-full border border-control-border user-invalid:border-button-danger" />
                </FormElement>

                <!-- Only rendered when the org actually has a sandbox; with a single
                     option there is no choice to present. -->
                <FormElement v-if="home_options.length > 1" title="Where should this demo account live?">
                    <BaseSelect v-model="form.home" :options="homeSelectOptions" />
                </FormElement>

                <div>
                    <BaseButton theme="primary" type="submit" :disabled="submitting">
                        {{ submitting ? "Creating…" : "Create demo account" }}
                    </BaseButton>
                </div>
            </form>
        </section>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { Head, router, usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import { formatDate } from "~vue/utils/formatDate";
import { computed, reactive, ref } from "vue";

export interface DemoAccountRow {
    id: string;
    label: string;
    job_title: string;
    organization_name: string;
    last_used_at: string | null;
    masquerade_url: string;
}

export interface HomeOption {
    value: string;
    label: string;
}

const props = defineProps<{
    demo_accounts: DemoAccountRow[];
    home_options: HomeOption[];
    max_demo_accounts: number;
    text_max_length: number;
    endpoints: { create: string; current_page: string };
}>();

const page = usePage<{ errors?: Record<string, string> }>();

const submitting = ref(false);
const csrfToken = getCookie("csrftoken");

const form = reactive({
    first_name: "",
    last_name: "",
    job_title: "",
    home: props.home_options[0]?.value ?? "organization",
});

const atLimit = computed(() => props.demo_accounts.length >= props.max_demo_accounts);
const listHeading = computed(() => (props.demo_accounts.length === 1 ? "Your demo account" : "Your demo accounts"));
// A partial unique constraint on owner fixes the limit at one, so there is no
// plural case to word.
const atLimitMessage = "You already have a demo account. Ask your Valence contact if you need to delete this demo account.";
const homeSelectOptions = computed(() => props.home_options.map((option) => ({ key: option.value, label: option.label })));

// The create view reports refusals through Django's message framework rather than
// per-field errors, so surface whichever message came back.
const formError = computed(() => {
    const errors = page.props.errors ?? {};
    const values = Object.values(errors);
    return values.length ? values[0] : "";
});

function lastUsedLabel(account: DemoAccountRow): string {
    if (!account.last_used_at) {
        return "Never used";
    }
    return `Last used ${formatDate(account.last_used_at, "date")}`;
}

// CSRF_USE_SESSIONS means there is no csrftoken cookie for axios to pick up; the
// token is rendered into <meta name="csrf-token">, which getCookie reads, and every
// router.post has to attach it by hand.
function csrfHeaders() {
    return { "X-CSRFToken": getCookie("csrftoken") ?? "" };
}

function submit() {
    submitting.value = true;
    router.post(
        props.endpoints.create,
        { ...form },
        {
            headers: csrfHeaders(),
            onSuccess: () => {
                form.first_name = "";
                form.last_name = "";
                form.job_title = "";
            },
            onFinish: () => (submitting.value = false),
        },
    );
}
</script>
